import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import {
  SprayCan,
  Plus,
  Pencil,
  Loader2,
  ScanLine,
  Droplets,
  Wrench,
  CheckCircle,
} from 'lucide-react';

interface PaintBooth {
  id: string;
  code: string;
  name: string;
  nfc_uid: string | null;
  capacity: number;
  status: 'available' | 'in_use' | 'maintenance';
  current_work_order_id: string | null;
  started_at: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  pieces_count?: number;
  current_wo?: {
    order_number: string;
    product_name: string;
  };
}

interface BoothPiece {
  id: string;
  work_order_id: string;
  entered_at: string;
  status: string;
  work_order?: {
    order_number: string;
    product_name: string;
    customer: string;
  };
}

const STATUS_COLORS: Record<string, string> = {
  available: 'bg-green-100 text-green-800',
  in_use: 'bg-blue-100 text-blue-800',
  maintenance: 'bg-gray-100 text-gray-800',
};

const STATUS_LABELS: Record<string, string> = {
  available: 'Disponible',
  in_use: 'En uso',
  maintenance: 'Mantenimiento',
};

const emptyForm = {
  code: '',
  name: '',
  nfc_uid: '',
  capacity: 10,
  is_active: true,
};

export default function PaintBooths() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [booths, setBooths] = useState<PaintBooth[]>([]);

  // Formulario
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBooth, setEditingBooth] = useState<PaintBooth | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Detalle de cabina
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedBooth, setSelectedBooth] = useState<PaintBooth | null>(null);
  const [boothPieces, setBoothPieces] = useState<BoothPiece[]>([]);
  const [loadingPieces, setLoadingPieces] = useState(false);

  // Confirmación mantenimiento
  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Cargar cabinas con OT actual
      const { data: boothsData, error: boothsError } = await api
        .from('paint_booths')
        .select(`
          *,
          current_wo:work_orders!paint_booths_current_work_order_id_fkey(
            order_number, product_name
          )
        `)
        .order('code');

      if (boothsError) throw boothsError;

      // Contar piezas por cabina
      const { data: piecesCount } = await api
        .from('paint_booth_pieces')
        .select('paint_booth_id')
        .is('removed_at', null);

      const countMap: Record<string, number> = {};
      (piecesCount || []).forEach((p: any) => {
        countMap[p.paint_booth_id] = (countMap[p.paint_booth_id] || 0) + 1;
      });

      const boothsWithCount = (boothsData || []).map((b: PaintBooth) => ({
        ...b,
        pieces_count: countMap[b.id] || 0,
      }));

      setBooths(boothsWithCount);
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Error cargando cabinas',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreateDialog = () => {
    setEditingBooth(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEditDialog = (booth: PaintBooth) => {
    setEditingBooth(booth);
    setForm({
      code: booth.code,
      name: booth.name,
      nfc_uid: booth.nfc_uid || '',
      capacity: booth.capacity,
      is_active: booth.is_active,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      toast({
        title: 'Error',
        description: 'Código y nombre son obligatorios',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        code: form.code.trim(),
        name: form.name.trim(),
        nfc_uid: form.nfc_uid.trim() || null,
        capacity: form.capacity,
        is_active: form.is_active,
      };

      if (editingBooth) {
        const { error } = await api
          .from('paint_booths')
          .update(payload)
          .eq('id', editingBooth.id);
        if (error) throw error;
        toast({ title: 'Cabina actualizada' });
      } else {
        const { error } = await api
          .from('paint_booths')
          .insert(payload);
        if (error) throw error;
        toast({ title: 'Cabina creada' });
      }

      setDialogOpen(false);
      loadData();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const openDetailDialog = async (booth: PaintBooth) => {
    setSelectedBooth(booth);
    setDetailDialogOpen(true);
    setLoadingPieces(true);

    try {
      const { data, error } = await api
        .from('paint_booth_pieces')
        .select(`
          *,
          work_order:work_orders(order_number, product_name, customer)
        `)
        .eq('paint_booth_id', booth.id)
        .is('removed_at', null)
        .order('entered_at');

      if (error) throw error;
      setBoothPieces(data || []);
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setLoadingPieces(false);
    }
  };

  const handleSetMaintenance = async () => {
    if (!selectedBooth) return;

    setSaving(true);
    try {
      const { error } = await api
        .from('paint_booths')
        .update({
          status: 'maintenance',
          current_work_order_id: null,
        })
        .eq('id', selectedBooth.id);

      if (error) throw error;

      toast({
        title: 'Cabina en mantenimiento',
        description: `${selectedBooth.name} marcada para mantenimiento`,
      });

      setMaintenanceDialogOpen(false);
      setDetailDialogOpen(false);
      loadData();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSetAvailable = async (booth: PaintBooth) => {
    try {
      const { error } = await api
        .from('paint_booths')
        .update({ status: 'available' })
        .eq('id', booth.id);

      if (error) throw error;

      toast({
        title: 'Cabina disponible',
        description: `${booth.name} lista para usar`,
      });

      loadData();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive',
      });
    }
  };

  const activeBooths = booths.filter(b => b.is_active);
  const inactiveBooths = booths.filter(b => !b.is_active);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <SprayCan className="h-6 w-6" />
              Cabinas de Pintura
            </h1>
            <p className="text-muted-foreground">
              Gestión de cabinas para el proceso de pintura
            </p>
          </div>
          <Button onClick={openCreateDialog}>
            <Plus className="h-4 w-4 mr-2" />
            Nueva Cabina
          </Button>
        </div>

        {/* Resumen */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {activeBooths.filter(b => b.status === 'available').length}
                  </p>
                  <p className="text-sm text-muted-foreground">Disponibles</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Droplets className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {activeBooths.filter(b => b.status === 'in_use').length}
                  </p>
                  <p className="text-sm text-muted-foreground">En uso</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gray-100 rounded-full">
                  <Wrench className="h-6 w-6 text-gray-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {activeBooths.filter(b => b.status === 'maintenance').length}
                  </p>
                  <p className="text-sm text-muted-foreground">Mantenimiento</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabla de cabinas */}
        <Card>
          <CardHeader>
            <CardTitle>Cabinas Activas</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>NFC</TableHead>
                    <TableHead>Capacidad</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Piezas</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeBooths.map((booth) => (
                    <TableRow key={booth.id}>
                      <TableCell className="font-mono">{booth.code}</TableCell>
                      <TableCell>{booth.name}</TableCell>
                      <TableCell>
                        {booth.nfc_uid ? (
                          <code className="text-xs bg-muted px-1 py-0.5 rounded">
                            {booth.nfc_uid}
                          </code>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>{booth.capacity}</TableCell>
                      <TableCell>
                        <Badge className={STATUS_COLORS[booth.status]}>
                          {STATUS_LABELS[booth.status]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {booth.pieces_count || 0}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {booth.status === 'maintenance' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSetAvailable(booth)}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openDetailDialog(booth)}
                          >
                            Ver
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(booth)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Cabinas inactivas */}
        {inactiveBooths.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-muted-foreground">
                Cabinas Inactivas ({inactiveBooths.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableBody>
                  {inactiveBooths.map((booth) => (
                    <TableRow key={booth.id} className="opacity-50">
                      <TableCell>{booth.code}</TableCell>
                      <TableCell>{booth.name}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(booth)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Diálogo crear/editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingBooth ? 'Editar Cabina' : 'Nueva Cabina'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Código *</Label>
                <Input
                  placeholder="CAB-01"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Capacidad</Label>
                <Input
                  type="number"
                  min={1}
                  value={form.capacity}
                  onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 10 })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input
                placeholder="Cabina Principal"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <ScanLine className="h-4 w-4" />
                NFC UID
              </Label>
              <Input
                placeholder="Escanea o ingresa el UID del tag NFC..."
                value={form.nfc_uid}
                onChange={(e) => setForm({ ...form, nfc_uid: e.target.value })}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>Cabina activa</Label>
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingBooth ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo detalle */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <SprayCan className="h-5 w-5" />
              {selectedBooth?.code} - {selectedBooth?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <span>Estado:</span>
              <Badge className={STATUS_COLORS[selectedBooth?.status || 'available']}>
                {STATUS_LABELS[selectedBooth?.status || 'available']}
              </Badge>
            </div>

            <div className="flex items-center justify-between">
              <span>Capacidad:</span>
              <span>{selectedBooth?.capacity} piezas</span>
            </div>

            {selectedBooth?.nfc_uid && (
              <div className="flex items-center justify-between">
                <span>NFC:</span>
                <code className="text-xs bg-muted px-2 py-1 rounded">
                  {selectedBooth.nfc_uid}
                </code>
              </div>
            )}

            {/* Piezas en cabina */}
            <div className="border-t pt-4">
              <h4 className="font-semibold mb-2">Piezas en cabina</h4>
              {loadingPieces ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : boothPieces.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sin piezas</p>
              ) : (
                <div className="space-y-2">
                  {boothPieces.map((piece) => (
                    <div
                      key={piece.id}
                      className="flex justify-between items-center p-2 bg-muted rounded"
                    >
                      <div>
                        <p className="font-medium text-sm">
                          {piece.work_order?.order_number}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {piece.work_order?.product_name}
                        </p>
                      </div>
                      <Badge variant="secondary">{piece.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            {selectedBooth?.status !== 'maintenance' && (
              <Button
                variant="outline"
                onClick={() => setMaintenanceDialogOpen(true)}
              >
                <Wrench className="h-4 w-4 mr-2" />
                Marcar Mantenimiento
              </Button>
            )}
            <Button variant="ghost" onClick={() => setDetailDialogOpen(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmación mantenimiento */}
      <AlertDialog open={maintenanceDialogOpen} onOpenChange={setMaintenanceDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Marcar como mantenimiento</AlertDialogTitle>
            <AlertDialogDescription>
              La cabina {selectedBooth?.code} quedará fuera de servicio hasta que
              la marques como disponible nuevamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleSetMaintenance} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
